import React, { useMemo } from "react";
import { Button, Tooltip } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useAgentsStore } from "../../../../../../../../../../store/agents.store";
import {
    selectAgentHierarchy,
    selectHiddenFields,
} from "../../../../../../../../../../store/agentDetail/agentDetailSelectors";
import { ConfirmationModal } from "../../../../../../../confirmationModal/confirmationModal.index";
import { AgentHierarchyTree } from "./agentHierarchyTree.index";
import { useHierarchyActions } from "./hierarchy.hooks";
import { findHierarchyNode } from "./hierarchy.utils";
import styles from "./hierarchy.module.css";

const NO_DOWNLINE_USERS_TOOLTIP = "No downline available.";

export const Hierarchy = () => {
    const { contractAndLicensingInformation } = useAgentsStore(selectHiddenFields);
    const hierarchyInfo = useAgentsStore(selectAgentHierarchy);
    const activeUserId = useAgentsStore((state) => state.detail.activeUserId);

    const isActivateDownlineHidden = contractAndLicensingInformation.includes("activateDownLine");
    const isInactivateDownlineHidden = contractAndLicensingInformation.includes("inactivateDownLine");
    const isDownloadHierarchyHidden = contractAndLicensingInformation.includes("downloadHierarchy");

    const noDownlineHierarchy = useMemo(() => {
        if (!activeUserId) return true;
        const activeNode = findHierarchyNode(hierarchyInfo, activeUserId);
        return !activeNode || activeNode.downLine.length === 0;
    }, [hierarchyInfo, activeUserId]);

    const {
        isProcessing,
        isDownloading,
        pendingActivate,
        handleActivateClick,
        handleInactivateClick,
        handleConfirmCancel,
        handleConfirmSubmit,
        handleDownloadHierarchy,
    } = useHierarchyActions();

    const isActionDisabled = noDownlineHierarchy || isProcessing || isDownloading;

    const wrapWithTooltip = (node: React.ReactNode) =>
        noDownlineHierarchy ? (
            <Tooltip title={NO_DOWNLINE_USERS_TOOLTIP}>{node}</Tooltip>
        ) : (
            node
        );

    return (
        <div className={styles.outerCtn}>
            <div className={styles.headerCtn}>
                <div className={styles.leftCtn}>
                    <h2 className={styles.title}>Hierarchy</h2>
                </div>
                <div className={styles.rightCtn}>
                    {!isActivateDownlineHidden &&
                        wrapWithTooltip(
                            <Button
                                color="primary"
                                variant="solid"
                                onClick={handleActivateClick}
                                loading={isProcessing}
                                disabled={isActionDisabled}
                            >
                                Activate Downline
                            </Button>
                        )}
                    {!isInactivateDownlineHidden &&
                        wrapWithTooltip(
                            <Button
                                variant="solid"
                                // danger
                                color="orange"
                                onClick={handleInactivateClick}
                                loading={isProcessing}
                                disabled={isActionDisabled}
                            >
                                Inactivate Downline
                            </Button>
                        )}
                    {!isDownloadHierarchyHidden &&
                        wrapWithTooltip(
                            <Button
                                onClick={handleDownloadHierarchy}
                                loading={isDownloading}
                                disabled={isActionDisabled}
                                icon={<DownloadOutlined />}
                                variant="outlined"
                                color="primary"
                            >
                                Download Downline Hierarchy
                            </Button>
                        )}
                </div>
            </div>
            <AgentHierarchyTree />
            <ConfirmationModal
                open={pendingActivate !== null}
                saving={isProcessing}
                onCancel={handleConfirmCancel}
                onSave={handleConfirmSubmit}
                title={pendingActivate ? "Activate Downline" : "Inactivate Downline"}
                message={
                    pendingActivate
                        ? "Are you sure you want to activate all downline users for this agent?"
                        : "Are you sure you want to inactivate all downline users for this agent?"
                }
                confirmButtonText={pendingActivate ? "Activate" : "Inactivate"}
            />
        </div>
    );
};
